import { useSnackbar } from 'notistack';
import { useState } from 'react';
import {
  createSubModule as createSubModuleService,
  updateSubModule as updateSubModuleService,
  updateLevelAssessmentaArea as updateLevelAssessmentaAreaService,
  deleteSubModule as deleteSubModuleService,
  updateSetting as updateSettingService
} from '../../services/periodical-report/periodical-report-setting.service.js';
import { capitalizarPrimeraLetra, validate } from '../../utils/index.js';
import { handlerErrorV2 } from '../../utils/errors.jsx';
import isEmpty from 'lodash/isEmpty';

const usePeriodicalReportSetting = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(false);

  const validateForm = (form, type) => {
    let fieldRules = {};

    if (['period', 'type'].includes(type)) {
      fieldRules = {
        name: 'required',
        code: 'required|withoutSpaces'
      };
    }
    if (['level'].includes(type)) {
      fieldRules = {
        name: 'required',
        code: 'required|withoutSpaces',
        position: 'required'
      };
    }
    if (type === 'basic-information') {
      fieldRules = {
        name: 'required',
        version: 'required'
      };
    }
    if (type === 'assessment-area') {
      fieldRules = {
        name: 'required',
        code: 'required|withoutSpaces',
        type: 'required',
        elementId: 'required',
        levels: 'required'
      }
    }
    if (type === 'assessment-area-level') {
      fieldRules = {
        definition: 'required'
      }
    }

    const errors = validate(fieldRules, form);
    if (!isEmpty(errors)) {
      return { error: true, ...errors };
    }

    return { error: false };
  };

  const formData = (form, type) => {
    if (type === 'type') {
      return {
        code: form.code,
        name: form.name,
        description: form.description,
        curriculumId: form.curriculumId
      }
    }

    if (type === 'level') {
      return {
        code: form.code,
        name: form.name,
        position: form.position,
        description: form.description,
        curriculumId: form.curriculumId,
        curriculumLevelsId: form.curriculumLevelsId
      }
    }

    if (type === 'period') {
      return {
        code: form.code,
        name: form.name,
        description: form.description,
      }
    }

    if (type === 'assessment-area') {
      return {
        code: form.code,
        name: form.name,
        type: form.type,
        description: form.description,
        elementId: form.elementId,
        subElementId: form.subElementId,
        groupElement: form.groupElement,
        levels: form.levels,
        curriculumId: form.curriculumId
      }
    }

    if (type === 'assessment-area-level') {
      return {
        definition: form.definition
      }
    }

    return form;
  };

  const createSubModule = async (form = {}, type) => {
    const result = validateForm(form, type);
    if (result.error) {
      return result;
    }

    try {
      const data = formData(form, type);
      const response = await createSubModuleService(data, type);
      if (!response.data && response.error) {
        return {
          error: true,
          message: response.error.message || 'An error ocurred'
        };
      } else {
        enqueueSnackbar(`${capitalizarPrimeraLetra(type)} created successfully`, {
          variant: 'success'
        });

        return { error: false };
      }
    } catch (error) {
      const message = handlerErrorV2(error);
      return { error: true, message };
    }
  };

  const updateSubModule = async (settingId, type, form = {}) => {
    const result = validateForm(form, type);
    if (result.error) {
      return result;
    }

    try {
      const data = formData(form, type);
      const response = await updateSubModuleService(settingId, type, data);
      if (!response.data && response.error) {
        return {
          error: true,
          message: response.error.message || 'An error ocurred'
        };
      } else {
        enqueueSnackbar(`${capitalizarPrimeraLetra(type)} updated successfully`, {
          variant: 'success'
        });
        return { error: false };
      }
    } catch (error) {
      const message = handlerErrorV2(error);
      return { error: true, message };
    }
  };

  const deleteSubModule = async (settingId, type) => {
    setIsLoading(true);
    try {
      const response = await deleteSubModuleService(settingId, type);
      if (!response.data && response.error) {
        return {
          error: true,
          message: response.error.message || 'An error ocurred'
        };
      } else {
        enqueueSnackbar(`${capitalizarPrimeraLetra(type)} deleted successfully`, {
          variant: 'success'
        });

        return { error: false };
      }
    } catch (error) {
      const message = handlerErrorV2(error);
      return { error: true, message };
    } finally {
      setIsLoading(false);
    }
  };

  const updateLevelAssessmentArea = async (settingId, levelId, form) => {
    const result = validateForm(form, 'assessment-area-level');
    if (result.error) {
      return result;
    }

    try {
      const data = formData(form, 'assessment-area-level');
      const response = await updateLevelAssessmentaAreaService(settingId, levelId, data);
      if (!response.data && response.error) {
        return {
          error: true,
          message: response.error.message || 'An error ocurred'
        };
      } else {
        // enqueueSnackbar(`${capitalizarPrimeraLetra(type)} updated successfully`, {
        //   variant: 'success'
        // });
        return { error: false };
      }
    } catch (error) {
      const message = handlerErrorV2(error);
      return { error: true, message };
    }
  }

  const updateSetting = async (form = {}) => {
    const result = validateForm(form, 'basic-information');
    if (result.error) {
      return result;
    }

    try {
      const data = {
        name: form.name,
        version: form.version,
        schoolEntity: form.schoolEntity,
        schoolNumber: form.schoolNumber
      }
      const response = await updateSettingService(data);
      if (!response.data && response.error) {
        return {
          error: true,
          message: response.error.message || 'An error ocurred'
        };
      } else {
        enqueueSnackbar('Updated successfully', {
          variant: 'success'
        });
        return { error: false };
      }
    } catch (error) {
      const message = handlerErrorV2(error);
      return { error: true, message };
    }
  };

  return {
    isLoading,
    createSubModule,
    updateSubModule,
    updateLevelAssessmentArea,
    deleteSubModule,
    updateSetting
  };
};

export default usePeriodicalReportSetting;
